import { useEffect, useState } from "react";
import { CalendarRange, Clock, Zap } from "lucide-react";

import { Modal } from "@/components/Modal";
import { useUi, type RelativeSpec } from "@/lib/store";
import {
  RELATIVE_UNITS,
  describeDuration,
  describeRelative,
  fromLocalInput,
  shift,
  toLocalInput,
  type RelativeUnit,
} from "@/lib/timeRange";

const NUDGES: { label: string; count: number; unit: RelativeUnit }[] = [
  { label: "-1D", count: 1, unit: "days" },
  { label: "-1W", count: 1, unit: "weeks" },
  { label: "-1M", count: 1, unit: "months" },
  { label: "-1Y", count: 1, unit: "years" },
];

const DEFAULT_RELATIVE: RelativeSpec = { count: 7, unit: "days", fromCount: 0, fromUnit: "days" };

/**
 * An absolute window, or one defined relative to now.
 *
 * Both are applied to every chart. A relative range is stored as its
 * definition, so "the previous 7 days" still means that on tomorrow's visit
 * rather than freezing on the dates it was set.
 */
export function TimeRangeDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { range, setCustomRange, setRelativeRange } = useUi();
  const [startText, setStartText] = useState("");
  const [endText, setEndText] = useState("");
  const [relative, setRelative] = useState<RelativeSpec>(DEFAULT_RELATIVE);

  useEffect(() => {
    if (!open) return;
    setStartText(toLocalInput(range.start));
    setEndText(toLocalInput(range.end));
    setRelative(range.relative ?? DEFAULT_RELATIVE);
  }, [open, range]);

  const start = fromLocalInput(startText);
  const end = fromLocalInput(endText);
  const customError = !start || !end ? "Enter both dates." : start >= end ? "Start must be before end." : null;

  function nudge(which: "start" | "end", count: number, unit: RelativeUnit) {
    const current = which === "start" ? start : end;
    if (!current) return;
    const moved = toLocalInput(shift(current, -count, unit));
    if (which === "start") setStartText(moved);
    else setEndText(moved);
  }

  function applyCustom() {
    if (customError || !start || !end) return;
    setCustomRange(start, end);
    onClose();
  }

  const relativeValid = relative.count >= 1 && relative.fromCount >= 0;

  function applyRelative() {
    if (!relativeValid) return;
    setRelativeRange(relative);
    onClose();
  }

  return (
    <Modal
      title="Time Range"
      open={open}
      onClose={onClose}
      width="max-w-xl"
      footer={
        <div className="flex w-full items-center justify-between">
          <span className="flex items-center gap-1.5 text-xs text-ink-400">
            <Clock className="h-3.5 w-3.5" />
            Duration: {start && end && !customError ? describeDuration(start, end) : describeDuration(range.start, range.end)}
          </span>
          <button type="button" className="btn-ghost" onClick={onClose}>
            Close
          </button>
        </div>
      }
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <div className="label flex items-center gap-1.5">
            <CalendarRange className="h-3.5 w-3.5" />
            Custom range
          </div>
          <DateField
            label="Start"
            value={startText}
            onChange={setStartText}
            onNudge={(count, unit) => nudge("start", count, unit)}
          />
          <DateField
            label="End"
            value={endText}
            onChange={setEndText}
            onNudge={(count, unit) => nudge("end", count, unit)}
          />
          {customError && <p className="text-[11px] text-sev-1">{customError}</p>}
          <button
            type="button"
            className="btn-ghost w-full justify-center"
            onClick={applyCustom}
            disabled={Boolean(customError)}
          >
            Apply custom range
          </button>
        </section>

        <section className="space-y-3 border-t border-ink-700/60 pt-5">
          <div className="label flex items-center gap-1.5">
            <Zap className="h-3.5 w-3.5" />
            Relative
          </div>
          <RelativeRow
            label="Previous"
            count={relative.count}
            unit={relative.unit}
            min={1}
            onChange={(count, unit) => setRelative({ ...relative, count, unit })}
          />
          <RelativeRow
            label="Ending"
            suffix="ago"
            count={relative.fromCount}
            unit={relative.fromUnit}
            min={0}
            onChange={(fromCount, fromUnit) => setRelative({ ...relative, fromCount, fromUnit })}
          />
          <p className="text-center text-xs text-ink-400">{describeRelative(relative)}</p>
          <button
            type="button"
            className="btn-ghost w-full justify-center"
            onClick={applyRelative}
            disabled={!relativeValid}
          >
            Apply relative range
          </button>
        </section>
      </div>
    </Modal>
  );
}

function DateField({
  label,
  value,
  onChange,
  onNudge,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  onNudge: (count: number, unit: RelativeUnit) => void;
}) {
  return (
    <div>
      <label className="block">
        <span className="label mb-1 block">{label}</span>
        <input
          type="datetime-local"
          className="input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
        />
      </label>
      <div className="mt-1.5 flex justify-center gap-4">
        {NUDGES.map((n) => (
          <button
            key={n.label}
            type="button"
            className="text-[11px] font-medium text-brand-300 hover:text-brand-200"
            onClick={() => onNudge(n.count, n.unit)}
            title={`Move ${label.toLowerCase()} back ${n.count} ${n.unit.slice(0, -1)}`}
          >
            {n.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function RelativeRow({
  label,
  suffix,
  count,
  unit,
  min,
  onChange,
}: {
  label: string;
  suffix?: string;
  count: number;
  unit: RelativeUnit;
  min: number;
  onChange: (count: number, unit: RelativeUnit) => void;
}) {
  return (
    <div className="flex items-center gap-2">
      <span className="w-16 text-sm text-ink-300">{label}</span>
      <input
        type="number"
        min={min}
        className="input w-24"
        value={count}
        onChange={(e) => onChange(Math.max(min, Math.floor(Number(e.target.value) || 0)), unit)}
        aria-label={`${label} amount`}
      />
      <select
        className="input flex-1"
        value={unit}
        onChange={(e) => onChange(count, e.target.value as RelativeUnit)}
        aria-label={`${label} unit`}
      >
        {RELATIVE_UNITS.map((u) => (
          <option key={u} value={u}>
            {u}
          </option>
        ))}
      </select>
      {suffix && <span className="w-8 text-sm text-ink-400">{suffix}</span>}
    </div>
  );
}
